import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import {
  getTurnstileToken,
  resetTurnstileToken,
  turnstileIsConfigured,
} from '../lib/turnstileToken';
import type {
  Developer,
  Project,
  PropertyDraft,
  PropertyType,
  SavedProperty,
} from '../types/property';

export async function getOrCreateGuestUser(captchaToken?: string): Promise<User> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    throw sessionError;
  }

  if (sessionData.session?.user) {
    return sessionData.session.user;
  }

  const effectiveCaptchaToken = captchaToken ?? getTurnstileToken() ?? undefined;
  if (turnstileIsConfigured() && !effectiveCaptchaToken) {
    throw new Error('Complete the human verification challenge before starting a new guest session.');
  }

  try {
    const { data, error } = await supabase.auth.signInAnonymously(
      effectiveCaptchaToken ? { options: { captchaToken: effectiveCaptchaToken } } : undefined,
    );

    if (error || !data.user) {
      throw error ?? new Error('Supabase did not return an anonymous user.');
    }

    return data.user;
  } finally {
    if (effectiveCaptchaToken) resetTurnstileToken();
  }
}

export async function getExistingUser(): Promise<User | null> {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    throw error;
  }

  return data.session?.user ?? null;
}

export async function loadDevelopers(): Promise<Developer[]> {
  const { data, error } = await supabase
    .from('developers')
    .select('id, display_name, aliases')
    .eq('status', 'active')
    .order('display_name');

  if (error) {
    throw error;
  }

  return (data ?? []) as Developer[];
}

export async function loadProjects(
  developerId: string,
  propertyType: PropertyType,
): Promise<Project[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('id, developer_id, project_name, aliases, locality, property_types')
    .eq('developer_id', developerId)
    .in('project_status', ['active', 'upcoming', 'completed'])
    .contains('property_types', [propertyType])
    .order('project_name');

  if (error) {
    throw error;
  }

  return (data ?? []) as Project[];
}

export async function loadLatestProperty(
  ownerId: string,
): Promise<SavedProperty | null> {
  const { data, error } = await supabase
    .from('properties')
    .select(
      'id, property_type, developer_id, project_id, manual_developer_name, manual_project_name, locality, tower_or_block, unit_or_villa_number, bedrooms, bathrooms, inspection_stage, updated_at',
    )
    .eq('owner_id', ownerId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as SavedProperty | null) ?? null;
}

export async function saveProperty(
  ownerId: string,
  draft: PropertyDraft,
  existingPropertyId: string | null,
): Promise<SavedProperty> {
  const isManual = !draft.projectId;

  if (isManual) {
    const { error: submissionError } = await supabase
      .from('manual_project_submissions')
      .insert({
        owner_id: ownerId,
        developer_name: draft.manualDeveloperName.trim(),
        project_name: draft.manualProjectName.trim(),
        city: 'Hyderabad',
        locality: draft.locality.trim() || null,
        property_type: draft.propertyType,
      });

    if (submissionError) {
      throw submissionError;
    }
  }

  const payload = {
    owner_id: ownerId,
    property_type: draft.propertyType,
    city: 'Hyderabad',
    developer_id: draft.projectId ? draft.developerId : null,
    project_id: draft.projectId,
    project_phase_id: null,
    manual_developer_name: draft.projectId
      ? null
      : draft.manualDeveloperName.trim(),
    manual_project_name: draft.projectId ? null : draft.manualProjectName.trim(),
    locality: draft.locality.trim() || null,
    tower_or_block: draft.towerOrBlock.trim() || null,
    unit_or_villa_number: draft.unitOrVillaNumber.trim() || null,
    bedrooms: draft.bedrooms,
    bathrooms: draft.bathrooms,
    inspection_stage: draft.inspectionStage,
  };

  const query = existingPropertyId
    ? supabase
        .from('properties')
        .update(payload)
        .eq('id', existingPropertyId)
        .eq('owner_id', ownerId)
    : supabase.from('properties').insert(payload);

  const { data, error } = await query
    .select(
      'id, property_type, developer_id, project_id, manual_developer_name, manual_project_name, locality, tower_or_block, unit_or_villa_number, bedrooms, bathrooms, inspection_stage, updated_at',
    )
    .single();

  if (error) {
    throw error;
  }

  return data as SavedProperty;
}
